import { ArrowDownwardOutlined, ArrowUpwardOutlined, DragIndicatorOutlined, ErrorOutline } from '@mui/icons-material';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, TableCell, Tooltip, styled } from '@mui/material';
import { DeleteIcon, EditIcon, ExtendedMenuIcon } from '@synergy/theme-next';
import { DuplicateIcon } from '@synergy/theme';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { MouseEvent, useState } from 'react';
import { PaloAmount, PaloDirtyCell, PaloQuantityCell } from '@synergy/components-palo';
import { PoolNumberTextfield } from '@synergy/components-next';
import { ProductDetails } from '@synergy/components';
import { UserRole } from '@synergy/models';
import { hasRole } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import urlJoin from 'url-join';

import { FinancialCalculations } from '../../../../../../common/utils/financial-calculations';
import { InvoiceProductItem } from '../../../../models/invoice-product-item';
import { PaloPositionText } from '../../../../../../common/components/palo/palo-position-text';
import { PaloRowProductSearch } from '../../../../../../common/components/palo/palo-row-product-search';
import { PositionSelection, PositionSelectionBox } from '../../../../../../common/components/palo/position-selection';
import { Product } from '../../../../../../common/models/product';
import { ROUTES } from '../../../../../sidebar-menu/navigation-drawer/routes';
import { ROW_HANDLE_SX, RowDrag, rowDragHandleProps } from '../../../../../../common/components/palo/position-row-drag';
import { RowPosition } from './detail-positions-table';
import { TaxRate } from '../../../../../../common/models/tax-rate';
import { TaxRateAutocomplete } from '../../../../../../common/components/tax-rate-autocomplete/tax-rate-autocomplete';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { getProductPrice } from '../../../../../../common/utils/get-product-price';
import { isCreditSelector } from '../../../store/positions/invoice-detail-positions-selectors';
import { positionFieldNames } from '../../../models/positions-form-values';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { userSelector } from '../../../../../../common/selectors/user-selectors';

export interface DetailPositionsTableProductRowProps {
    index: number;
    item: InvoiceProductItem;
    readonly: boolean;
    taxRates: TaxRate[];
    currency: string;
    /** Whether this is the last row, whose quantity takes the focus. */
    position: RowPosition;
    /** Restricts the in-row article search to the supplier of the document. */
    supplierIds?: number[];
    /** For the message when the supplier carries nothing that matches. */
    supplierName?: string;
    /** No supplier chosen in the head yet. */
    supplierMissing?: boolean;
    /** Fills a blank row with the article that was searched for. */
    onPickProduct?: (index: number, product: Product) => void;
    onTaxRateSelected: (index: number, taxRate: TaxRate | null) => void;
    /** Set while the row books to an account another country is meant for (R-175). */
    accountHint?: string;
    /** Opens the position's own dialog, from the opened description. */
    onEdit?: () => void;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(0, 1),
}));

export const DetailPositionsTableProductRow = (props: DetailPositionsTableProductRowProps) => {
    const {
        index,
        item,
        taxRates,
        readonly = false,
        currency,
        position,
        supplierIds,
        supplierName,
        supplierMissing,
        onPickProduct,
        onTaxRateSelected,
        accountHint,
        onEdit,
    } = props;
    const locale = useAppSelector(bestLocaleSelector);
    const url = item.product && urlJoin(ROUTES.products.product.base, item.product.id.toString());
    const user = useAppSelector(userSelector);
    const isCredit = useAppSelector(isCreditSelector);
    const prefix = positionFieldNames.allItems + '.' + index;
    // A row that carries neither an article nor a name of its own is one that
    // was just added and is waiting for one.
    const isBlank = !item.product && 0 === Object.values(item.name ?? {}).filter(Boolean).length;

    const handleQuantityChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        form: FormikProps<any>,
        field: FieldProps<number>['field']
    ): void => {
        const quantity = e.target.value;
        // The scale of the article is in positive prices; a credit note keeps
        // its sign, or every change of quantity turned a refund into a charge.
        const current = form.getFieldProps(prefix + '.price').value;
        const scaled = getProductPrice(
            isCredit ? Math.abs(current ?? 0) : current,
            'EUR',
            Number(quantity),
            item.product
        );
        const price = isCredit ? -Math.abs(scaled) : scaled;
        form.setFieldValue(field.name, quantity);
        form.setFieldValue(prefix + '.price', price);
    };

    return (
        <>
            <StyledTableCell>
                {/* A row added with "Zeile hinzufügen" starts empty, and the
                    article is searched for right here — the way ERPNext fills
                    a new row in the row itself (#2208). */}
                {isBlank && onPickProduct ? (
                    <PaloRowProductSearch
                        onSelected={product => onPickProduct(index, product)}
                        supplierIds={supplierIds}
                        supplierName={supplierName}
                        supplierMissing={supplierMissing}
                        includeNormArticles={false}
                    />
                ) : (
                    <>
                        <ProductDetails
                            product={item}
                            showDescription={false}
                            inlineNumber={true}
                            maxLines={2}
                            url={hasRole([UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales], user) ? url : undefined}
                        />
                        <PaloPositionText
                            index={index}
                            shortDescription={item.shortDescription}
                            longDescription={item.longDescription}
                            onEdit={readonly ? undefined : onEdit}
                        />
                    </>
                )}
            </StyledTableCell>
            <StyledTableCell>
                {/* The unit keeps a column of its own beside the figure; inside
                    the field it pushed the number about from row to row. */}
                <PaloQuantityCell unit={item.orderUnit}>
                    <PaloDirtyCell name={prefix + '.quantity'}>
                        <Field name={prefix + '.quantity'}>
                            {({ field, form, meta }: FieldProps<number>) => (
                                <PoolNumberTextfield
                                    {...field}
                                    id={field.name}
                                    onChange={e => handleQuantityChange(e, form, field)}
                                    type="dense"
                                    error={!!meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                    readonly={readonly}
                                    autoFocus={position === 'last' && !isBlank}
                                    textAlign="right"
                                />
                            )}
                        </Field>
                    </PaloDirtyCell>
                </PaloQuantityCell>
            </StyledTableCell>
            <StyledTableCell>
                <PaloDirtyCell name={prefix + '.price'}>
                    <Field name={prefix + '.price'}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolNumberTextfield
                                {...field}
                                value={field.value}
                                id={field.name}
                                type="dense"
                                error={!!meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                readonly={readonly}
                                fullWidth={true}
                                currency={currency}
                                currencySymbol={false}
                                locale={locale}
                                textAlign="right"
                                sx={{ width: 96 }}
                            />
                        )}
                    </Field>
                </PaloDirtyCell>
            </StyledTableCell>
            <StyledTableCell>
                <PaloDirtyCell name={prefix + '.discount'}>
                    <Field name={prefix + '.discount'}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolNumberTextfield
                                {...field}
                                onBlur={field.onBlur}
                                value={field.value}
                                type="dense"
                                error={!!meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                readonly={readonly}
                                suffix=" %"
                                textAlign="right"
                                sx={{ width: 58 }}
                            />
                        )}
                    </Field>
                </PaloDirtyCell>
            </StyledTableCell>
            <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {accountHint && (
                        <Tooltip title={accountHint} placement="top">
                            <ErrorOutline
                                aria-label={accountHint}
                                sx={{ fontSize: 16, color: tokens.color.warn, flexShrink: 0 }}
                            />
                        </Tooltip>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TaxRateAutocomplete
                            display="short"
                            name={prefix + '.taxRate'}
                            disabled={readonly}
                            taxRates={taxRates}
                            onValueChange={taxRate => onTaxRateSelected(index, taxRate)}
                            showLabel={false}
                            textAlign="right"
                        />
                    </Box>
                </Box>
            </StyledTableCell>
            <StyledTableCell>
                <PaloDirtyCell name={prefix + '.tax'}>
                    <Field name={prefix + '.tax'}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolNumberTextfield
                                {...field}
                                type="dense"
                                error={!!meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                readonly={readonly}
                                suffix=" %"
                                textAlign="right"
                                sx={{ width: 58 }}
                            />
                        )}
                    </Field>
                </PaloDirtyCell>
            </StyledTableCell>
            <StyledTableCell>
                {/* Fixed width so the amounts line up; pushed right so the
                    column ends where its heading does. */}
                <Box sx={{ whiteSpace: 'pre', width: 86, marginLeft: 'auto', textAlign: 'right' }}>
                    <PaloAmount value={FinancialCalculations.getNetTotal(item)} />
                </Box>
            </StyledTableCell>
        </>
    );
};
